import type { RosterEntry } from '@/types/domain';

/** Post-test pass mark (ACLS). Mirrors course.passScore; kept here so the pure
 *  scoreboard logic is testable without a course object. */
export const PASS_SCORE = 80;

/** Board-only "so close" band floor (70–79). Does NOT touch the certificate
 *  gate — it only softens how the big screen shows a near-miss. */
export const CLOSE_SCORE = 70;

/** Stable identity for a roster row — userId in production, emp in mock. Used as
 *  the React key and for the "who just arrived" diff on live refetch. */
export function entryKey(r: RosterEntry): string {
  return r.userId ?? r.emp;
}

export type Tier = 'pass' | 'close' | 'below';

/** A finished learner's board band. `posttest` is assumed non-null (a finisher).
 *  `pass` (≥80) is the real certificate gate; `close` (70–79) is a board-only
 *  courtesy so near-passers are acknowledged without exposing a low number. */
export function tier(r: RosterEntry, pass = PASS_SCORE, close = CLOSE_SCORE): Tier {
  const s = r.posttest as number;
  if (s >= pass) return 'pass';
  if (s >= close) return 'close';
  return 'below';
}

/** Learners who have submitted the post-test, in the given (stable) order. */
export function finishers(roster: RosterEntry[]): RosterEntry[] {
  return roster.filter((r) => r.posttest != null);
}

export interface ScoreboardAggregate {
  /** submitted the post-test */
  finished: number;
  /** roster size (enrolled) */
  total: number;
  /** finished AND >= pass */
  passed: number;
  /** passed / finished, rounded % */
  passRate: number;
  /** mean post-test of finishers, rounded */
  avg: number;
}

export function aggregate(roster: RosterEntry[], pass = PASS_SCORE): ScoreboardAggregate {
  const done = finishers(roster);
  const finished = done.length;
  const passed = done.filter((r) => (r.posttest as number) >= pass).length;
  const avg = finished ? Math.round(done.reduce((a, r) => a + (r.posttest as number), 0) / finished) : 0;
  return {
    finished,
    total: roster.length,
    passed,
    passRate: finished ? Math.round((passed / finished) * 100) : 0,
    avg,
  };
}

export type SuperlativeKind = 'top' | 'improved' | 'perfect';

export interface Superlative {
  kind: SuperlativeKind;
  icon: string;
  label: { en: string; th: string };
  /** headline figure, e.g. "96", "+41", "×3" */
  value: string;
  /** the celebrated learner(s) — one, except Perfect which lists all */
  winners: RosterEntry[];
}

/** Fun, face-saving highlights that spread the spotlight beyond the top scorer.
 *  Every card is omitted when it has no qualifying learner (e.g. nobody improved),
 *  so we never project an empty award. Ties resolve to the first finisher. */
export function computeSuperlatives(roster: RosterEntry[]): Superlative[] {
  const done = finishers(roster);
  if (done.length === 0) return [];
  const out: Superlative[] = [];

  // 🏆 Top score — strict `>` keeps the first of any tie.
  const top = done.reduce((best, r) => ((r.posttest as number) > (best.posttest as number) ? r : best));
  out.push({ kind: 'top', icon: '🏆', label: { en: 'Top score', th: 'คะแนนสูงสุด' }, value: `${top.posttest}`, winners: [top] });

  // 🚀 Most improved — biggest pre→post jump, only when positive.
  const improvers = done.filter((r) => r.pretest != null);
  if (improvers.length) {
    const delta = (r: RosterEntry) => (r.posttest as number) - (r.pretest as number);
    const best = improvers.reduce((b, r) => (delta(r) > delta(b) ? r : b));
    if (delta(best) > 0) {
      out.push({ kind: 'improved', icon: '🚀', label: { en: 'Most improved', th: 'พัฒนาขึ้นมากสุด' }, value: `+${delta(best)}`, winners: [best] });
    }
  }

  // 💯 Perfect score(s).
  const perfect = done.filter((r) => (r.posttest as number) >= 100);
  if (perfect.length) {
    out.push({
      kind: 'perfect',
      icon: '💯',
      label: { en: 'Perfect score', th: 'คะแนนเต็ม' },
      value: perfect.length > 1 ? `×${perfect.length}` : `${perfect[0].posttest}`,
      winners: perfect,
    });
  }

  return out;
}

/* ── Pre-test ("starting line") — a baseline snapshot, NOT a pass/fail board.
 *  A diagnostic has no passing mark, so the pre board stays neutral and warm. ── */

export interface BaselineAggregate {
  /** took the pre-test */
  done: number;
  total: number;
  /** mean pre-test of those who took it, rounded */
  avg: number;
}

export function preFinishers(roster: RosterEntry[]): RosterEntry[] {
  return roster.filter((r) => r.pretest != null);
}

export function baseline(roster: RosterEntry[]): BaselineAggregate {
  const rows = preFinishers(roster);
  const done = rows.length;
  const avg = done ? Math.round(rows.reduce((a, r) => a + (r.pretest as number), 0) / done) : 0;
  return { done, total: roster.length, avg };
}

/** Pre-test highlights — celebrate curiosity and a strong start, never a "pass". */
export function computePreSuperlatives(roster: RosterEntry[]): Superlative[] {
  const done = preFinishers(roster);
  if (done.length === 0) return [];
  const out: Superlative[] = [];

  const top = done.reduce((best, r) => ((r.pretest as number) > (best.pretest as number) ? r : best));
  out.push({ kind: 'top', icon: '🧠', label: { en: 'Top starter', th: 'มาแรงตั้งแต่ต้น' }, value: `${top.pretest}`, winners: [top] });

  const perfect = done.filter((r) => (r.pretest as number) >= 100);
  if (perfect.length) {
    out.push({
      kind: 'perfect',
      icon: '💯',
      label: { en: 'Perfect start', th: 'เต็มตั้งแต่ก่อนเรียน' },
      value: perfect.length > 1 ? `×${perfect.length}` : `${perfect[0].pretest}`,
      winners: perfect,
    });
  }
  return out;
}

/** Keys present among finishers in `next` but not in `prev` — the learners who
 *  just appeared since the last refetch, so only they animate in. */
export function newArrivals(prevKeys: Iterable<string>, next: RosterEntry[]): Set<string> {
  const seen = new Set(prevKeys);
  const fresh = new Set<string>();
  for (const r of finishers(next)) {
    const k = entryKey(r);
    if (!seen.has(k)) fresh.add(k);
  }
  return fresh;
}
